import React from "react";
import cx from "classnames";
import _ from "lodash";
import posthog from "posthog-js";
import { Box, MenuItem, MenuSubmenu, Separator, Switch, Text } from "@showitinc/showit-wc";
import AppConstants from "../../../constants/AppConstants";
import UIActions from "../../../actions/UIActions";
import SiteActions from "../../../actions/SiteActions";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import UserActions from "../../../actions/UserActions";
import LayoutChooserActions from "../../../actions/LayoutChooserActions";
import ClipboardStore from "../../../stores/ClipboardStore";
import ContextMenuWrapper from "../../../app2components/ContextMenus/ContextMenuWrapper";
import BlockUpdateVO from "../../../vo/BlockUpdate";
import BlockUtils from "../../../utils/BlockUtils";
import uiPrefix from "../../../utils/UiPrefixUtils";
import { addTextElement, addElement, addButton, addOvalElement } from "../../../utils/elementCreationUtils";
import LayerPanelFormFieldsSubmenu from "./LayerPanelFormFieldsSubmenu";
import styles from "./LayerPanelBlockMenu.module.css";

const {
  ITEM_TYPE_BLOCK,
  VISIBLE_DESKTOP,
  VISIBLE_ALL,
  VISIBLE_MOBILE,
  VISIBLE_NONE,
  CONTEXT_ITEM_BUTTON,
  BLOCK_PROP_PARENT_BLOCK,
  TEXT_STYLE_TITLE,
  TEXT_STYLE_HEADING,
  TEXT_STYLE_SUBHEADING,
  TEXT_STYLE_PARAGRAPH,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_SOCIAL_GRID,
  BLOCK_PROP_SELECTED_LAYOUT_PATH,
  LAYOUT_DESKTOP,
} = AppConstants;

const CLICK_LOCATION = "layer-panel-context-menu";

class LayerPanelBlockMenu extends React.Component {
  constructor(props) {
    super(props);
    let pasteLabel = "";
    let pasteEnabled = false;
    if (ClipboardStore.canPasteBlock()) {
      pasteEnabled = true;
      pasteLabel = ClipboardStore.getContentLabel();
    }

    this.state = {
      dropArea: null,
      pasteEnabled,
      pasteLabel,
    };
  }

  componentDidMount() {
    this.unsubscribeStore = ClipboardStore.listen(this._onClipboardChange);
  }

  componentWillUnmount() {
    if (this.unsubscribeStore) {
      _.attempt(this.unsubscribeStore);
    }
  }

  _selectSiteBlock = () => {
    const { block } = this.props;

    const baseBlock = block.get(BLOCK_PROP_PARENT_BLOCK);

    UIActions.selectSiteBlock({ block: baseBlock });
    UIActions.addActiveItemHistory({
      type: ITEM_TYPE_BLOCK,
    });
  };

  _addText = (style) => {
    addTextElement(style, { blockId: this.props.block.get("id") }, CLICK_LOCATION);
  };

  _addElement = (elementType) => {
    addElement(elementType, { blockId: this.props.block.get("id") }, CLICK_LOCATION);
  };

  _addButton = () => {
    addButton({ blockId: this.props.block.get("id") }, CLICK_LOCATION);
  };

  _addOval = () => {
    addOvalElement({ blockId: this.props.block.get("id") }, CLICK_LOCATION);
  };

  _onClipboardChange = () => {
    let pasteLabel = "";
    let pasteEnabled = false;
    if (ClipboardStore.canPasteBlock()) {
      pasteEnabled = true;
      pasteLabel = ClipboardStore.getContentLabel();
    }

    this.setState({ pasteEnabled, pasteLabel });
  };

  _remove = () => {
    const { block } = this.props;

    DocumentStore.dispatch({ type: COMMANDS.REMOVE_BLOCK, payload: { block } });
  };

  _duplicate = () => {
    const { block } = this.props;

    DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_BLOCK, payload: { id: block.get("id") } });
  };

  _copy = () => {
    const { block } = this.props;
    SiteActions.copy({ id: block.get("id"), type: ITEM_TYPE_BLOCK });
  };

  _cut = () => {
    const { block } = this.props;
    SiteActions.copy({ id: block.get("id"), type: ITEM_TYPE_BLOCK, cut: true });
  };

  _convertToSiteBlock = () => {
    const { block } = this.props;
    DocumentStore.dispatch({ type: COMMANDS.CONVERT_TO_SITE_BLOCK, payload: { id: block.get("id") } });
  };

  _convertToPageBlock = () => {
    const { block } = this.props;
    DocumentStore.dispatch({ type: COMMANDS.CONVERT_TO_PAGE_BLOCK, payload: { id: block.get("id") } });
  };

  _layoutMobile = () => {
    const { block } = this.props;
    LayoutChooserActions.openDialog({ id: block.get("id") });
  };

  _rename = () => {
    const { block } = this.props;
    const isSiteBlock = BlockUtils.isSiteBlock(block);

    UIActions.toggleRenamePopup({
      title: `${isSiteBlock ? "Site " : ""}Canvas`,
      itemType: ITEM_TYPE_BLOCK,
      itemId: block.get("id"),
      name: block.get("name"),
      slug: block.get("slug"),
      showWarning: true,
    });
  };

  _pasteInto = () => {
    const { block } = this.props;
    DocumentStore.dispatch({
      type: COMMANDS.PASTE_INTO,
      payload: { id: block.get("id"), type: ITEM_TYPE_BLOCK },
    });
  };

  _pasteAbove = () => {
    const { block } = this.props;
    const blockIndex = _.get(this.props, "blockIndex", 0);
    DocumentStore.dispatch({
      type: COMMANDS.PASTE,
      payload: {
        id: block.get("id"),
        insertAt: blockIndex,
        type: ITEM_TYPE_BLOCK,
      },
    });
  };

  _pasteBelow = () => {
    const { block } = this.props;
    let blockIndex = _.get(this.props, "blockIndex", 0);
    blockIndex += 1;
    DocumentStore.dispatch({
      type: COMMANDS.PASTE,
      payload: {
        id: block.get("id"),
        insertAt: blockIndex,
        type: ITEM_TYPE_BLOCK,
      },
    });
  };

  _addState = () => {
    const { block } = this.props;
    DocumentStore.dispatch({ type: COMMANDS.ADD_BLOCK_STATE, payload: { id: block.get("id") } });
  };

  _optimizeLayerOrder = () => {
    const { block } = this.props;
    const layout = block.getIn(BLOCK_PROP_SELECTED_LAYOUT_PATH) || LAYOUT_DESKTOP;
    UserActions.trackPostHogEvent("layer_order_optimize_clicked", {
      scope: "block",
      layout,
    });
    DocumentStore.dispatch({
      type: COMMANDS.SORT_BLOCK_BY_READING_ORDER,
      payload: { blockId: block.get("id"), layout },
    });
  };

  _updateVisible = (visibility) => {
    const { block } = this.props;
    const id = block.get("id");
    const visible = block.get("visible");

    switch (visibility) {
      case VISIBLE_DESKTOP:
        switch (visible) {
          case VISIBLE_ALL:
            visibility = VISIBLE_MOBILE;
            break;
          case VISIBLE_DESKTOP:
            visibility = VISIBLE_NONE;
            break;
          case VISIBLE_MOBILE:
            visibility = VISIBLE_ALL;
            break;
          case VISIBLE_NONE:
            visibility = VISIBLE_DESKTOP;
            break;
          default:
            break;
        }
        break;
      case VISIBLE_MOBILE:
        switch (visible) {
          case VISIBLE_ALL:
            visibility = VISIBLE_DESKTOP;
            break;
          case VISIBLE_DESKTOP:
            visibility = VISIBLE_ALL;
            break;
          case VISIBLE_MOBILE:
            visibility = VISIBLE_NONE;
            break;
          case VISIBLE_NONE:
            visibility = VISIBLE_MOBILE;
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    const update = new BlockUpdateVO();
    update.add(id, "visible", visibility);

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  render() {
    const { block, theme } = this.props;
    const { pasteEnabled } = this.state;

    let pasteIntoAction = null;
    let pasteAboveAction = null;
    let pasteBelowAction = null;
    let pasteLabel = "Paste";

    if (pasteEnabled) {
      pasteIntoAction = this._pasteInto;
      pasteBelowAction = this._pasteBelow;
      pasteAboveAction = this._pasteAbove;
      pasteLabel = `Paste "${this.state.pasteLabel}"`;
    }
    const visibility = block.get("visible");

    const isSiteBlock = BlockUtils.isSiteBlock(block);
    const hasSiteBlock = BlockUtils.hasSiteBlock(block);

    let editSiteCanvas = null;
    let convertToPageBlock = null;
    let convertToSiteBlock = null;
    let pasteInto = null;
    let addCanvasView = null;
    let layoutMobile = null;

    if (hasSiteBlock) {
      editSiteCanvas = (
        <MenuItem
          data-e2e={uiPrefix(`edit-site-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={this._selectSiteBlock}
        >
          Edit Site Canvas
        </MenuItem>
      );
    }

    if (!isSiteBlock && hasSiteBlock) {
      convertToPageBlock = (
        <MenuItem
          data-e2e={uiPrefix(`convert-page-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={this._convertToPageBlock}
        >
          Convert To Page Canvas
        </MenuItem>
      );
    }

    if (!isSiteBlock && !hasSiteBlock) {
      convertToSiteBlock = (
        <MenuItem
          data-e2e={uiPrefix(`convert-site-canvas-${CONTEXT_ITEM_BUTTON}`)}
          onClick={this._convertToSiteBlock}
        >
          Convert To Site Canvas
        </MenuItem>
      );
      pasteInto = (
        <MenuItem
          data-e2e={uiPrefix(`paste-into-${CONTEXT_ITEM_BUTTON}`)}
          onClick={pasteIntoAction}
          title="Copied canvas content replaces the content in this canvas"
          disabled={!pasteEnabled}
        >
          {`${pasteLabel} Into`}
        </MenuItem>
      );
    }

    if (!hasSiteBlock) {
      addCanvasView = (
        <MenuItem
          data-e2e={uiPrefix(`add-canvas-view-${CONTEXT_ITEM_BUTTON}`)}
          onClick={this._addState}
        >
          Add Canvas View
        </MenuItem>
      );

      if (visibility === VISIBLE_ALL) {
        layoutMobile = (
          <>
            <Separator variant="context-menu" />
            <MenuItem
              data-e2e={uiPrefix(`layout-mobile-${CONTEXT_ITEM_BUTTON}`)}
              onClick={this._layoutMobile}
            >
              Layout Mobile
            </MenuItem>
          </>
        );
      }
    }

    return (
      <ContextMenuWrapper icon="menuMoreHoriz" active={this.props.active}>
        {!hasSiteBlock && (
        <MenuSubmenu label="Add Text">
          <MenuItem
            data-e2e={uiPrefix(`add-text-title-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="textElement"
            onClick={() => this._addText(TEXT_STYLE_TITLE)}
          >
            Title
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-text-heading-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="textElement"
            onClick={() => this._addText(TEXT_STYLE_HEADING)}
          >
            Heading
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-text-subheading-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="textElement"
            onClick={() => this._addText(TEXT_STYLE_SUBHEADING)}
          >
            Subheading
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-text-paragraph-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="textElement"
            onClick={() => this._addText(TEXT_STYLE_PARAGRAPH)}
          >
            Paragraph
          </MenuItem>
        </MenuSubmenu>
        )}
        {!hasSiteBlock && (
        <MenuSubmenu label="Add Element">
          <MenuItem
            data-e2e={uiPrefix(`add-element-line-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="lineElement"
            onClick={() => this._addElement(ELEMENT_TYPE_LINE)}
          >
            Line
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-rectangle-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="squareElement"
            onClick={() => this._addElement(ELEMENT_TYPE_SIMPLE)}
          >
            Rectangle
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-oval-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="ellipse"
            onClick={this._addOval}
          >
            Oval
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-icon-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="symbolElement"
            onClick={() => this._addElement(ELEMENT_TYPE_ICON)}
          >
            Icon
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-button-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="buttonElement"
            onClick={this._addButton}
          >
            Button
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-image-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="mediaLibrary"
            onClick={() => this._addElement(ELEMENT_TYPE_GRAPHIC)}
          >
            Image
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-gallery-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="galleryElement"
            onClick={() => this._addElement(ELEMENT_TYPE_GALLERY)}
          >
            Gallery
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-video-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="videoElement"
            onClick={() => this._addElement(ELEMENT_TYPE_VIDEO)}
          >
            Video
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-lottie-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="lottieFiles"
            onClick={() => this._addElement(ELEMENT_TYPE_LOTTIE)}
          >
            Lottie
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-social-grid-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="socialGridElement"
            onClick={() => this._addElement(ELEMENT_TYPE_SOCIAL_GRID)}
          >
            Social Grid
          </MenuItem>
          <MenuItem
            data-e2e={uiPrefix(`add-element-embed-${CONTEXT_ITEM_BUTTON}`)}
            variant="icon"
            icon="embedElement"
            onClick={() => this._addElement(ELEMENT_TYPE_IFRAME)}
          >
            Embed Code
          </MenuItem>
        </MenuSubmenu>
        )}
        {!hasSiteBlock && (
          <>
            <LayerPanelFormFieldsSubmenu
              blockId={this.props.block.get("id")}
              clickLocation={CLICK_LOCATION}
            />
            <Separator variant="context-menu" />
          </>
        )}
        <MenuItem data-e2e={uiPrefix(`rename-${CONTEXT_ITEM_BUTTON}`)} onClick={this._rename}>
          Rename
        </MenuItem>
        {addCanvasView}
        {editSiteCanvas}
        <Separator variant="context-menu" />
        {isSiteBlock ? null : (
          <MenuItem
            data-e2e={uiPrefix(`duplicate-${CONTEXT_ITEM_BUTTON}`)}
            onClick={this._duplicate}
          >
            {hasSiteBlock ? "Duplicate as Page Canvas" : "Duplicate"}
          </MenuItem>
        )}
        {isSiteBlock ? null : (
          <MenuItem data-e2e={uiPrefix(`copy-${CONTEXT_ITEM_BUTTON}`)} onClick={this._copy}>
            Copy
          </MenuItem>
        )}
        {isSiteBlock ? null : (
          <MenuItem data-e2e={uiPrefix(`cut-${CONTEXT_ITEM_BUTTON}`)} onClick={this._cut}>
            Cut
          </MenuItem>
        )}
        <Separator variant="context-menu" />
        <MenuItem data-e2e={uiPrefix(`visible-desktop-${CONTEXT_ITEM_BUTTON}`)} si-keep-open>
          <Switch
            label="Visible On Desktop"
            onClick={() => this._updateVisible(VISIBLE_DESKTOP)}
            checked={visibility === VISIBLE_ALL || visibility === VISIBLE_DESKTOP}
            noPadding
          >
            <Text slot="label" className={styles["switch-label"]}>
              Visible On Desktop
            </Text>
          </Switch>
        </MenuItem>
        <MenuItem data-e2e={uiPrefix(`visible-mobile-${CONTEXT_ITEM_BUTTON}`)} si-keep-open>
          <Switch
            label="Visible On Mobile"
            onClick={() => this._updateVisible(VISIBLE_MOBILE)}
            checked={visibility === VISIBLE_ALL || visibility === VISIBLE_MOBILE}
            noPadding
          >
            <Text slot="label" className={styles["switch-label"]}>
              Visible On Mobile
            </Text>
          </Switch>
        </MenuItem>
        {layoutMobile}
        {isSiteBlock ? null : (
          <MenuItem
            data-e2e={uiPrefix(`paste-above-${CONTEXT_ITEM_BUTTON}`)}
            onClick={pasteAboveAction}
            disabled={!pasteEnabled}
          >
            {`${pasteLabel} Above`}
          </MenuItem>
        )}
        {pasteInto}
        {isSiteBlock ? null : (
          <>
            <MenuItem
              data-e2e={uiPrefix(`paste-below-${CONTEXT_ITEM_BUTTON}`)}
              onClick={pasteBelowAction}
              disabled={!pasteEnabled}
            >
              {`${pasteLabel} Below`}
            </MenuItem>
            <Separator variant="context-menu" />
          </>
        )}
        {posthog.isFeatureEnabled("optimize-layer-order") && (
          <>
            <MenuItem
              data-testid="optimize-layer-order-block-menu"
              onClick={this._optimizeLayerOrder}
            >
              Optimize layer order (beta)
            </MenuItem>
            <Separator variant="context-menu" />
          </>
        )}
        {convertToSiteBlock}
        {convertToPageBlock}
        {isSiteBlock ? null : (
          <>
            <Separator variant="context-menu" />
            <MenuItem data-e2e={uiPrefix(`delete-${CONTEXT_ITEM_BUTTON}`)} onClick={this._remove}>
              <Box className={styles.delete}>Delete</Box>
            </MenuItem>
          </>
        )}
      </ContextMenuWrapper>
    );
  }
}

export default LayerPanelBlockMenu;
